/**
 * Fase 2B — DocumentVersionTimeline
 *
 * Toont de append-only `lifecycle_history` van een document als visuele tijdlijn.
 * Elke entry bevat action / status-overgang / actor / timestamp / reden.
 *
 * Werkt voor alle documenttypes (paklijst, boekingsformulier, waardefactuur).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, GitBranch, CheckCircle2, History, Copy, XCircle, RefreshCcw, Trash2, FileEdit } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import LifecycleStatusBadge from "./LifecycleStatusBadge";

const ACTION_META = {
  document_version_created: { icon: GitBranch, color: "text-slate-700", label: "Concept-versie aangemaakt" },
  document_version_activated: { icon: CheckCircle2, color: "text-emerald-700", label: "Versie geactiveerd" },
  document_replaced: { icon: History, color: "text-zinc-600", label: "Vervangen door nieuwere versie" },
  document_replaced_rollback: { icon: RefreshCcw, color: "text-amber-600", label: "Rollback naar actief" },
  document_marked_duplicate: { icon: Copy, color: "text-amber-700", label: "Gemarkeerd als duplicaat" },
  document_cancelled: { icon: XCircle, color: "text-rose-700", label: "Document geannuleerd" },
  document_restored: { icon: RefreshCcw, color: "text-emerald-700", label: "Hersteld naar actief" },
  document_deleted: { icon: Trash2, color: "text-rose-800", label: "Document verwijderd" },
  document_migrated: { icon: FileEdit, color: "text-slate-500", label: "Gemigreerd door systeem" },
  document_resolved_from_review_actief: { icon: CheckCircle2, color: "text-emerald-700", label: "Vanuit review-queue: actief" },
  document_resolved_from_review_duplicaat: { icon: Copy, color: "text-amber-700", label: "Vanuit review-queue: duplicaat" },
  document_resolved_from_review_geannuleerd: { icon: XCircle, color: "text-rose-700", label: "Vanuit review-queue: geannuleerd" },
  document_resolved_from_review_vervangen: { icon: History, color: "text-zinc-600", label: "Vanuit review-queue: vervangen" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DocumentVersionTimeline({ docType, docId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!docType || !docId) return;
    (async () => {
      setLoading(true);
      try {
        const [hR, dR] = await Promise.all([
          adminApi.get(`/admin/lifecycle/${docType}/${docId}/history`),
          adminApi.get(`/admin/lifecycle/${docType}/${docId}`),
        ]);
        setItems(hR.data?.items || []);
        setMeta(dR.data || null);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [docType, docId]);

  if (loading) {
    return (
      <div className="p-4 flex items-center gap-2 text-gray-500 text-xs">
        <Loader2 className="w-4 h-4 animate-spin" /> Tijdlijn laden...
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="document-version-timeline">
      {meta && (
        <div className="border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <LifecycleStatusBadge status={meta.lifecycle_status} />
            <span className="text-xs text-gray-600">
              Versie {meta.version_number || 1}
            </span>
            {meta.superseded_by_document_id && (
              <span className="text-xs text-gray-600">
                → vervangen door{" "}
                <code className="bg-white px-1 border">
                  {String(meta.superseded_by_document_id).slice(0, 8)}
                </code>
              </span>
            )}
            {meta.replaces_document_id && (
              <span className="text-xs text-gray-600">
                vervangt{" "}
                <code className="bg-white px-1 border">
                  {String(meta.replaces_document_id).slice(0, 8)}
                </code>
              </span>
            )}
            {meta.duplicate_of_document_id && (
              <span className="text-xs text-gray-600">
                duplicaat van{" "}
                <code className="bg-white px-1 border">
                  {String(meta.duplicate_of_document_id).slice(0, 8)}
                </code>
              </span>
            )}
          </div>
          {meta.lifecycle_reason && (
            <div className="text-[11px] text-gray-500 mt-2 italic">
              Reden: {meta.lifecycle_reason}
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-xs text-gray-500 italic p-4 text-center">
          Nog geen lifecycle-transities voor dit document.
        </div>
      ) : (
        <ol className="relative border-l-2 border-gray-200 ml-2 space-y-3">
          {items.map((entry, i) => {
            const meta = ACTION_META[entry.action] || {
              icon: FileEdit,
              color: "text-gray-600",
              label: entry.action,
            };
            const Icon = meta.icon;
            return (
              <li
                key={`${entry.timestamp}-${i}`}
                className="pl-5 relative"
                data-testid={`timeline-entry-${i}`}
              >
                <span className={`absolute -left-3 top-0.5 w-6 h-6 flex items-center justify-center bg-white border border-gray-300 ${meta.color}`}>
                  <Icon className="w-3 h-3" />
                </span>
                <div className="text-xs">
                  <div className="font-bold text-[#0A192F]">{meta.label}</div>
                  <div className="text-[11px] text-gray-500">
                    {fmtDate(entry.timestamp)} · {entry.actor || "onbekend"}
                  </div>
                  {(entry.previous_status || entry.new_status) && (
                    <div className="text-[11px] text-gray-600 mt-0.5">
                      {entry.previous_status ? (
                        <LifecycleStatusBadge status={entry.previous_status} size="sm" />
                      ) : (
                        <em>—</em>
                      )}
                      <span className="mx-1">→</span>
                      {entry.new_status ? (
                        <LifecycleStatusBadge status={entry.new_status} size="sm" />
                      ) : (
                        <em>—</em>
                      )}
                    </div>
                  )}
                  {entry.reason && (
                    <div className="text-[11px] text-gray-600 mt-1 italic">
                      {entry.reason}
                    </div>
                  )}
                  {entry.related_document_id && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      Gerelateerd document:{" "}
                      <code className="bg-white px-1 border">
                        {String(entry.related_document_id).slice(0, 8)}
                      </code>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
